import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ScheduledReportDefinitionFilter,
  ScheduledReportFieldDefinition,
  ScheduledReportValueOption,
} from '../../../services/scheduled-reports/scheduled-reports.service';

export interface PayrollDashboardReportParameterDialogData {
  reportName: string;
  filters: ScheduledReportDefinitionFilter[];
  fields: ScheduledReportFieldDefinition[];
  valueOptions?: Record<string, ScheduledReportValueOption[]>;
}

export type PayrollDashboardReportParameterDialogResult = Record<string, any>;

interface RuntimeParameterControl {
  key: string;
  label: string;
  type: string;
  operator: string;
  multiple: boolean;
  finiteOptionSource: boolean;
  options: ScheduledReportValueOption[];
}

@Component({
  selector: 'app-payroll-dashboard-report-parameters-dialog',
  templateUrl: './payroll-dashboard-report-parameters-dialog.component.html',
  styleUrls: ['./payroll-dashboard-report-parameters-dialog.component.scss'],
})
export class PayrollDashboardReportParametersDialogComponent {
  protected readonly controls: RuntimeParameterControl[];
  protected readonly form: UntypedFormGroup;

  constructor(
    private dialogRef: MatDialogRef<PayrollDashboardReportParametersDialogComponent, PayrollDashboardReportParameterDialogResult>,
    @Inject(MAT_DIALOG_DATA) protected data: PayrollDashboardReportParameterDialogData,
    formBuilder: UntypedFormBuilder,
  ) {
    this.controls = (data.filters || []).map((filter, index) => this.toRuntimeParameterControl(filter, index));
    this.form = formBuilder.group(this.controls.reduce((acc: Record<string, any>, control) => {
      acc[control.key] = [control.multiple && control.finiteOptionSource ? [] : '', Validators.required];
      return acc;
    }, {}));
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const raw = this.form.getRawValue();
    const values = this.controls.reduce((acc: Record<string, any>, control) => {
      const value = raw[control.key];
      acc[control.key] = control.multiple && control.finiteOptionSource
        ? this.normalizeStringArray(value)
        : control.multiple
        ? `${value || ''}`.split(/[\n,]/).map((item) => item.trim()).filter(Boolean)
        : this.parseValue(value, control.type);
      return acc;
    }, {});
    this.dialogRef.close(values);
  }

  private toRuntimeParameterControl(filter: ScheduledReportDefinitionFilter, index: number): RuntimeParameterControl {
    const field = this.data.fields.find((candidate) => candidate.key === filter.field);
    const finiteOptionSource = this.operatorUsesFiniteOptions(filter.operator)
      && Object.prototype.hasOwnProperty.call(this.data.valueOptions || {}, filter.field);
    const options = this.data.valueOptions?.[filter.field] || [];
    return {
      key: `${filter.parameterKey || `filter_${index + 1}`}`,
      label: field?.label || filter.field,
      type: field?.type || 'string',
      operator: filter.operator || 'equals',
      multiple: filter.operator === 'in' || filter.operator === 'not_in',
      finiteOptionSource,
      options,
    };
  }

  private normalizeStringArray(values: any): string[] {
    if (!Array.isArray(values)) {
      return [];
    }
    return values.map((value) => `${value || ''}`.trim()).filter(Boolean);
  }

  private operatorUsesFiniteOptions(operator?: string): boolean {
    return ['equals', 'not_equals', 'in', 'not_in'].includes(`${operator || ''}`);
  }

  private parseValue(value: any, type: string): any {
    if (type === 'number') {
      return Number(value);
    }
    if (type === 'boolean') {
      return `${value}` === 'true' ? 1 : 0;
    }
    return `${value || ''}`.trim();
  }
}
